import { api, requireAccount, withAccount } from "./core";
import type { UserVisibility } from "./disclosure";
import type { GalaxyViewDTO } from "./galaxy";

// OssVisibility is the caller's participation choice in EIS public research
// (oss-claim.md). BROADCAST-only: it decides whether a PUBLISHED artifact (the
// OSS Gravity Map, a public share card) may name them — it never changes the
// in-product galaxy view. removed = fully hidden; private = claimed but anonymous;
// name_only = name + role, no scores; full = name + scores.
export type OssVisibility = "removed" | "private" | "name_only" | "full";

// OssResearchEntry is one repo of the caller's OWN observed research gravity (self-
// access) — the "here's what claiming would show" motivator on /me/oss-presence.
export interface OssResearchEntry {
  repo: string;
  language_family: string;
  gravity: number;
  role: string;
}

// OssResearchRepo is one curated public-research TARGET repo (the OSS Gravity
// Map's observation scope). Curated public OSS — not galaxy data — so the slim
// shape carries only the name + language family; no internal id is exposed.
export interface OssResearchRepo {
  full_name: string;
  language_family: string;
}

// OssPresence is the caller's own participation state. login is their GitHub login
// (what a published surface shows when named); claimed is whether they have made
// any choice yet (false = the anonymous default, never decided). research is their
// own observed gravity in the public research repos (empty if they don't appear).
export interface OssPresence {
  ex_github_id: number;
  login: string;
  visibility: OssVisibility;
  claimed: boolean;
  research: OssResearchEntry[];
}

// ── Personal Gravity Card (self-observation; GET /me/gravity-card) ──────────
// The hero is the archetype histogram: a role is a distribution over time
// ("Architect ×14 · Anchor ×9"), not one frozen label. Self-observation only.
export interface GravityCardArchetype {
  name: string;
  count: number;
}
export interface GravityCardAxes {
  survival: number;
  production: number;
  catalysis: number;
  design: number;
  breadth: number;
  indispensability: number;
  debt_cleanup: number;
}
// GravityCardPeak is one high-water-mark window: the value plus the repo, period,
// and role it happened in. peak_gravity and peak_signal are DISTINCT windows (your
// best gravity month and best signal month need not share a period/repo).
export interface GravityCardPeak {
  value: number;
  period: string;
  role: string;
  repo: string;
  axes?: GravityCardAxes | null;
}
export interface GravityCardModule {
  repo: string;
  // roles: the author's role distribution WITHIN this repo (count DESC).
  roles: GravityCardArchetype[];
  // range: the tenure the record covers, "YYYY-MM – YYYY-MM" (or a single
  // "YYYY-MM"). Present on the career footprint (per-galaxy tenure); absent on the
  // research card (no tenure bounds).
  range?: string;
  // periods: how many trajectory windows (months) observed in this repo.
  periods: number;
  // ACCUMULATED over the trajectory (Σ per-window) — the career total in this repo,
  // which is what the card ranks and shows (the HEAD standing alone isn't a footprint).
  accum_gravity: number;
  accum_signal: number;
}
export interface GravityCard {
  login: string;
  avatar_url: string;
  ex_github_id: number;
  // observed is false when the caller is not (yet) in the research run — the FE
  // shows a "not observed yet" state instead of an empty card.
  observed: boolean;
  role: GravityCardArchetype[];
  style: GravityCardArchetype[];
  state: GravityCardArchetype[];
  primary_role: string;
  observed_periods: number;
  // active_months: distinct months with any surviving gravity (paired with accum).
  active_months: number;
  // observed_galaxies: distinct repo owners (a repo owner = a galaxy) observed in.
  observed_galaxies: number;
  lifetime_gravity: number;
  accum_gravity: number;
  accum_signal: number;
  peak_gravity: GravityCardPeak;
  peak_signal: GravityCardPeak;
  top_surviving: GravityCardModule[];
}

// ObservedGalaxy is one galaxy where the CURRENT user appears as a git author,
// with their own visibility there. Powers the self-service presence/erasure page.
export interface ObservedGalaxy {
  galaxy_id: number;
  login: string;
  avatar_url: string;
  visibility: UserVisibility;
}

// UserPreferences is the caller's cross-device display preferences. null = unset
// → the client keeps its browser default (Intl-detected zone / 'ja'). Server is
// the source of truth: the FE hydrates these on login and writes through on change.
export interface UserPreferences {
  locale: "ja" | "en" | null;
  timezone: string | null;
}

// ChronicleEntry is one entry of a past galaxy's tenure album.
export interface ChronicleEntry {
  period: string; // YYYY-MM
  lane: "memo" | "project" | "adr" | "event";
  title: string;
  body?: string;
}

// Affiliation is one galaxy the user USED TO belong to (a past membership), with
// their tenure and own visibility there. Membership-based ("where I belonged"),
// distinct from ObservedGalaxy ("where I'm observed").
export interface Affiliation {
  galaxy_id: number;
  login: string;
  avatar_url: string;
  role: string;
  joined_at: string; // RFC3339
  departed_at: string; // RFC3339
  visibility: UserVisibility;
}

// ActiveMembership is one galaxy the user CURRENTLY belongs to. The twin of
// Affiliation (departed) — its source for the "snapshot my current orbit" group.
// No departed_at: a current orbit is a dated snapshot, not a sealed tenure.
export interface ActiveMembership {
  galaxy_id: number;
  login: string;
  avatar_url: string;
  role: string;
  joined_at: string; // RFC3339
  visibility: UserVisibility;
}

// AffiliationDetail adds the gravity frozen at departure (survival decays after
// you leave, so this is the honest "what I left behind" figure — read from the
// snapshot history at-or-before departure). frozen_at is the period it was read.
export interface AffiliationDetail extends Affiliation {
  frozen_gravity: number;
  frozen_at?: string;
  // Peak gravity over the tenure + when it was reached. People wind down before
  // leaving, so the at-departure figure understates them — the peak is the
  // honest high-water mark.
  peak_gravity: number;
  peak_at?: string;
  // Living legacy decay (archived galaxy). raw_survival is the frozen tenure peak
  // ("what I built"). current_survival is the live, post-departure value when
  // legacy_decay_live is true (all three gates: org consent + Pro + self),
  // otherwise it equals raw_survival (frozen, decay 0). legacy_decay_pct is the
  // dissolved share (0..100). The certificate is ALWAYS frozen regardless.
  raw_survival: number;
  current_survival: number;
  legacy_decay_pct: number;
  legacy_decay_live: boolean;
  // The galaxy's admin consent (Alumni Legacy View), independent of the viewer's
  // plan. legacy_decay_live false + this true + viewer below Pro → invite Pro;
  // this false → stay frozen with no teaser (respect the org's choice).
  alumni_legacy_view: boolean;
  // Succession of what you stewarded (self-scoped). orphaned_gravity_pct is the
  // share (0..100) of the subject's OWN surviving module gravity now sitting in
  // modules with NO resident successor (still CollapseRisk ORPHANED);
  // orphaned_module_count is how many such modules. Gated identically to the
  // living-decay view (org consent + Pro) — 0 when frozen.
  orphaned_gravity_pct: number;
  orphaned_module_count: number;
}

// Career Orbit — signed observation sharing
// UniverseSummary mirrors backend model.UniverseSummary — the anonymized
// galaxy-scalar block carried on a certificate (D-03 safe at any N). It is what
// gives a raw gravity figure meaning: 60 in a 3-person collapsing galaxy is not
// 60 in a 50-person galaxy in steady orbit. top_share is k-anonymity gated (0
// below 5 contributors); archetype_mix/collapse_state may be omitted by the BE.
export interface UniverseSummary {
  contributors: number;
  size_bucket: string; // solo | small | team | large | massive
  total_gravity: number;
  archetype_mix?: Record<string, number>;
  top_share: number; // 0 when suppressed (< 5 contributors) or unknown
  collapse_state?: string; // stable | thinning | ""
}

export interface SignedObservation {
  id: number;
  source_account_id: number;
  source_account_login: string;
  target_github_login: string;
  target_user_id?: number;
  period_start: string;
  period_end: string;
  domains: string[];
  archetype_timeline: string[];
  universe_summary: UniverseSummary;
  // Certified tenure stats over the self row's per-period (gravity, signal)
  // series — signal = the 7-axis Total, gravity = Gravity. These are signed (cert
  // payload v2), so the card reads them rather than recomputing. signal_trajectory
  // is ascending by period {p:"YYYY-MM"; g; s}; may be empty (no in-tenure period).
  gravity_avg: number;
  gravity_peak: number;
  signal_avg: number;
  signal_peak: number;
  signal_trajectory: { p: string; g: number; s: number }[];
  // team_trajectory is the galaxy's per-period AVERAGE signal (mean 7-axis Total
  // over the period's core members) — the dashed reference line drawn behind the
  // subject's own orbit so "me vs the team's average" reads at a glance. Aggregate
  // + anonymous (a single mean, k-gated to >= 5 members BE-side), signal only, and
  // NOT part of the signed payload. Optional: certs minted before this field exists
  // carry no team line → the FE simply omits the dashed series (graceful).
  team_trajectory?: { p: string; s: number }[];
  status: string; // pending | accepted | revoked
  issued_by: number;
  accepted_at?: string;
  revoked_at?: string;
  signature: string;
  created_at: string;
}

// meApi is USER-scoped (not galaxy-scoped): no account_login on the path. It
// lets a person see every galaxy where they're observed and erase themselves
// from any of them — including galaxies they've LEFT, which the galaxy-scoped
// disclosureApi (membership-gated) cannot reach.
export const meApi = {
  // The caller's own GitHub OAuth connection health — drives the Control Room
  // "connected / re-auth needed" indicator. connected=false means the stored
  // token was revoked or expired (re-auth via /api/v1/auth/github).
  githubStatus: () => api.get<{ connected: boolean; login: string }>("/me/github"),
  observedGalaxies: () => api.get<ObservedGalaxy[]>("/me/observed-galaxies"),
  setVisibility: (galaxyId: number, visibility: UserVisibility) =>
    api.post<{ visibility: UserVisibility }>(`/me/galaxies/${galaxyId}/visibility`, { visibility }),
  // Past galaxies (departed memberships) — the switcher's "archive" section.
  affiliations: () => api.get<Affiliation[]>("/me/affiliations"),
  // Active memberships (galaxies the caller STILL belongs to). The dedicated
  // source for the "snapshot my current orbit" mint group — separate from
  // affiliations (departed) so the archive concept stays clean. Minting a snapshot
  // reuses the same subject-pull cert endpoint, idempotent + self-only.
  memberships: () => api.get<ActiveMembership[]>("/me/memberships"),
  // One past galaxy's record, including the caller's gravity FROZEN at departure.
  affiliation: (galaxyId: number) => api.get<AffiliationDetail>(`/me/affiliations/${galaxyId}`),
  // The past galaxy's timeline FROZEN to the caller's tenure — for the record's
  // trajectory + 世代交代 heatmap. Non-self members are anonymized (Member-N); the
  // caller's own row stays identified with real scores.
  affiliationTimeline: (galaxyId: number, fullAnon?: boolean, domain?: string) => {
    // domain scopes the 世代交代 heatmap to one sub-domain (BE/FE/...); empty =
    // the cross-domain rollup. anon=full forces every non-self member to Member-N.
    const params = new URLSearchParams();
    if (fullAnon) params.set("anon", "full");
    if (domain) params.set("domain", domain);
    const qs = params.toString();
    return api.get<GalaxyViewDTO[]>(`/me/affiliations/${galaxyId}/timeline${qs ? `?${qs}` : ""}`);
  },
  // The past galaxy's observed sub-domain names (BE/FE/Infra/...) — drives the
  // affiliation heatmap's domain tabs. Empty = no per-domain breakdown.
  affiliationDomains: (galaxyId: number) => api.get<string[]>(`/me/affiliations/${galaxyId}/domains`),
  // The past galaxy's chronicle FROZEN to the caller's tenure — the "album".
  // Self-related entries + galaxy-visible structural records (ADRs/projects).
  affiliationChronicle: (galaxyId: number) => api.get<ChronicleEntry[]>(`/me/affiliations/${galaxyId}/chronicle`),
  // Mint a shareable embed-card token (owner-only), minimal PER card type so each
  // embed URL stays under CloudFront's 8192-byte limit (the old single fat token
  // 414'd on multi-member galaxies). `type` selects which fields the token carries;
  // `fullAnon` forces Member-N on the handover heatmap. `handover_dropped` is how
  // many handover rows were trimmed to fit the URL budget (0 otherwise).
  affiliationCard: (galaxyId: number, opts?: { type?: string; fullAnon?: boolean }) => {
    const qs = [opts?.type ? `type=${encodeURIComponent(opts.type)}` : "", opts?.fullAnon ? "anon=full" : ""].filter(Boolean);
    return api.post<{ token: string; path: string; handover_dropped: number }>(
      `/me/affiliations/${galaxyId}/card${qs.length ? `?${qs.join("&")}` : ""}`,
      {},
    );
  },
  // Kill-switch for the self-contained affiliation-card embeds (owner-only). Bumps
  // the (member, galaxy) revocation version, so every /card/affiliation/:token/*
  // link the caller already pasted stops rendering; re-minting from the page issues
  // fresh links at the new version. 204 on success. See db/ace/schema.sql
  // (affiliation_card_versions).
  revokeAffiliationCards: (galaxyId: number) =>
    api.post<void>(`/me/affiliations/${galaxyId}/card/revoke`, {}),
  // Subject-pull issuance of a platform-signed Gravity Certificate for one galaxy
  // (owner-only). Status-aware idempotency: a DEPARTED member's certificate is
  // SEALED — a repeat call returns it unchanged. An ACTIVE member's dated snapshot
  // is REFRESHABLE — a repeat call RE-SNAPSHOTS it (period_end → this month, values
  // + signature recomputed) so the current orbit can be re-pulled before a 1:1.
  // The (re)minted certificate surfaces in Career Orbit (cross-galaxy timeline).
  // See docs/design/career-gravity-certificate.md.
  mintCertificate: (galaxyId: number) =>
    api.post<SignedObservation>(`/me/affiliations/${galaxyId}/certificate`, {}),
  // Read the already-issued certificate for one past galaxy (subject-pull read).
  // 404 (ApiError) when not a departed member OR not yet minted — the caller
  // treats the latter as "no cert → show the issue CTA". The certificate's homes
  // are both user-scoped: the affiliation record and /me/career.
  // See docs/design/career-gravity-certificate.md.
  certificate: (galaxyId: number) => api.get<SignedObservation>(`/me/affiliations/${galaxyId}/certificate`),
  // Display preferences (locale/timezone), cross-device. getPreferences hydrates
  // the client-persisted Phase-1 atoms from the server source of truth on login;
  // null fields mean "unset → keep the browser default". updatePreferences PATCHes
  // — an omitted field is left unchanged server-side; a bad value 400s.
  getPreferences: () => api.get<UserPreferences>("/me/preferences"),
  updatePreferences: (prefs: { locale?: "ja" | "en"; timezone?: string }) =>
    api.patch<UserPreferences>("/me/preferences", prefs),
  // OSS research participation (oss-claim.md): the caller's own choice of whether
  // a PUBLISHED artifact may name them. BROADCAST-only — never affects the
  // in-product galaxy view. ossPresence reads the current choice (default
  // anonymous when never decided); setOssPresence writes it (the caller can only
  // ever set their OWN identity — the server keys it off the JWT, never the body).
  ossPresence: () => api.get<OssPresence>("/me/oss-presence"),
  setOssPresence: (visibility: OssVisibility) =>
    api.put<OssPresence>("/me/oss-presence", { visibility }),
  // The caller's own personal Gravity Card — self-observation across the public
  // research repos they appear in (archetype histogram + lifetime/accum/peak +
  // top surviving repos). Self-access: keyed off the JWT, never the URL.
  gravityCard: () => api.get<GravityCard>("/me/gravity-card"),
  // The curated public-research TARGET repos (the OSS Gravity Map's observation
  // scope) — surfaces the existing research_repos registry so the participation
  // page can invite the people who contribute to them. Curated public OSS, not
  // galaxy data, so any authed caller may read it.
  ossResearchRepos: () => api.get<OssResearchRepo[]>("/oss/research-repos"),
};

// careerApi mixes galaxy-scoped (issued/revoke — keyed off the source
// galaxy) and user-scoped (pending/orbit/accept — keyed off the JWT login)
// endpoints. Galaxy-scoped methods require accountLogin. Issuance is no
// longer here: it moved to subject-pull mint (meApi.mintCertificate); the
// admin-push `share` endpoint was retired.
export const careerApi = {
  pending: () => api.get<SignedObservation[]>("/career/pending"),
  orbit: () => api.get<SignedObservation[]>("/career/orbit"),
  issued: (accountLogin: string) =>
    api.get<SignedObservation[]>(withAccount("/career/issued", requireAccount(accountLogin, "careerApi.issued"))),
  accept: (id: number) =>
    api.post<{ status: string }>("/career/accept", { id }),
  revoke: (accountLogin: string, id: number) =>
    api.post<{ status: string }>(withAccount("/career/revoke", requireAccount(accountLogin, "careerApi.revoke")), { id }),
};

// ── Public career card (career_shares) ──
//
// The subject's own cross-galaxy footprint frozen onto one shareable public page
// (/career/:token). User-scoped by JWT (/me/career-card) — the caller only ever
// mints/reads/revokes their OWN card. See docs/design/career-card-public.md.

// CareerShareState is one galaxy's display choice on the public card.
export type CareerShareState = "named" | "anon" | "excluded";

// CareerShareOption is one galaxy the caller holds a certificate for, offered to
// the editor with its public/private mode + the fail-closed default + current state.
export interface CareerShareOption {
  account_id: number;
  login: string;
  mode: "public" | "private";
  default_state: CareerShareState;
  current_state: CareerShareState;
}

// The frozen public payload (mirrors the backend CareerSharePayload). Anonymized
// galaxy rows carry NO login — only a positional label.
export interface CareerShareGalaxyRow {
  display: "named" | "anon";
  login?: string;
  label?: string;
  // range: the tenure the record covers, "YYYY-MM – YYYY-MM" (or a single "YYYY-MM").
  range?: string;
  accum_gravity: number;
  accum_signal: number;
  roles: { name: string; count: number }[];
}
export interface CareerSharePayload {
  login: string;
  ex_github_id: number;
  primary_role: string;
  accum_gravity: number;
  accum_signal: number;
  active_months: number;
  observed_galaxies: number;
  observed_periods: number;
  archetypes: { name: string; count: number }[];
  galaxies: CareerShareGalaxyRow[];
}
export interface CareerShare {
  id: number;
  token: string;
  status: string;
  payload: CareerSharePayload;
  revoked_at?: string | null;
}
export interface CareerShareView {
  share: CareerShare | null;
  options: CareerShareOption[];
}

export const careerCardApi = {
  // The caller's current card (share=null until first mint) + editable galaxy options.
  get: () => api.get<CareerShareView>("/me/career-card"),
  // (Re)mint with a per-galaxy selection; a galaxy omitted falls back to its
  // fail-closed default. Returns the frozen card + its public path (/career/:token).
  mint: (galaxies: { account_id: number; state: CareerShareState }[]) =>
    api.post<{ share: CareerShare; path: string }>("/me/career-card", { galaxies }),
  // Soft-revoke: the public page then shows a revoked notice.
  revoke: () => api.delete<void>("/me/career-card"),
};

